import {
  clearAuthRecoveryLatch,
  recoverFromAuthRedirect,
  responseIsProxyRedirect,
} from './session-recovery';

// Relative base: every env serves the SPA same-origin with the API and routes
// /api to the backend (vite dev proxies it), so the bundle bakes in no API URL.
const API_URL = '/api';

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public body?: unknown,
  ) {
    super(message);
  }
}

type RequestOptions = Omit<RequestInit, 'body'> & { json?: unknown };

export async function api<T = unknown>(path: string, options: RequestOptions = {}): Promise<T> {
  const { json, headers, ...rest } = options;
  const init: RequestInit = {
    ...rest,
    credentials: 'include',
    headers: {
      ...(json !== undefined ? { 'Content-Type': 'application/json' } : {}),
      Accept: 'application/json',
      ...headers,
    },
    body: json !== undefined ? JSON.stringify(json) : (rest as RequestInit).body,
  };

  const url = path.startsWith('http') ? path : `${API_URL}${path}`;

  let res: Response;
  try {
    res = await fetch(url, init);
  } catch (err) {
    // A rejected fetch is either a genuine network error or a cross-origin SSO
    // redirect the browser blocked. Probe to disambiguate; reload only if confirmed.
    await recoverFromAuthRedirect(false);
    throw err;
  }

  const contentType = res.headers.get('content-type') ?? '';

  if (!contentType.includes('application/json')) {
    // A JSON /api call that came back non-JSON and was redirected is the forward-auth
    // proxy serving an SSO page. Reload to complete the handshake at the top level.
    if (responseIsProxyRedirect(res)) {
      await recoverFromAuthRedirect(true);
    }
    throw new ApiError(res.status, 'Expected JSON response from API', await res.text());
  }

  const body = await res.json();

  if (!res.ok) {
    const message =
      (typeof body === 'object' && body !== null && 'message' in body && (body as any).message) ||
      `Request failed with status ${res.status}`;
    throw new ApiError(res.status, message, body);
  }

  clearAuthRecoveryLatch();
  return body as T;
}

export const apiBaseUrl = API_URL;
